import React, { useState, useEffect } from 'react';
import { henBatchesAPI, HenBatch, HenBatchSale } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../hooks/useTenant';
import { formatDateForTenant, calculateAgeFromDate } from '../utils/dateUtils';
import MortalityChart from '../components/HenBatches/MortalityChart';
import './HenBatchesPage.css';

const HenBatchesPage: React.FC = () => {
  const { currentTenant } = useAuth();
  const { tenant, timezone, dateFormat } = useTenant();
  const [batches, setBatches] = useState<HenBatch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingBatch, setEditingBatch] = useState<HenBatch | null>(null);
  const [showMortalityForm, setShowMortalityForm] = useState(false);
  const [showMortalityChart, setShowMortalityChart] = useState(false);
  const [selectedBatchId, setSelectedBatchId] = useState<number | null>(null);
  const [selectedBatchName, setSelectedBatchName] = useState<string>('');
  const [mortalityBatchId, setMortalityBatchId] = useState<number | null>(null);
  const [mortalityFormData, setMortalityFormData] = useState({
    mortality_date: new Date().toISOString().split('T')[0],
    count: 0,
    reason: '',
    notes: '',
  });
  const [showSaleForm, setShowSaleForm] = useState(false);
  const [saleBatchId, setSaleBatchId] = useState<number | null>(null);
  const [saleFormData, setSaleFormData] = useState({
    sale_date: new Date().toISOString().split('T')[0],
    count: 0,
    price_per_hen: 0,
    notes: '',
  });
  const [salesByBatch, setSalesByBatch] = useState<Record<number, HenBatchSale[]>>({});
  const [formData, setFormData] = useState({
    batch_name: '',
    initial_count: 0,
    current_count: 0,
    age_weeks: 0,
    age_days: 0,
    date_added: new Date().toISOString().split('T')[0],
    notes: '',
  });

  // Check if user has permission to manage batches (OWNER, CO_OWNER, ADMIN, MANAGER)
  const canAddBatch = Boolean(currentTenant && ['OWNER', 'CO_OWNER', 'ADMIN', 'MANAGER'].includes(currentTenant.role));

  useEffect(() => {
    fetchBatches();
  }, []);

  const fetchBatches = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await henBatchesAPI.getHenBatches();
      // Ensure data is always an array, never null or undefined
      const normalizedBatches = Array.isArray(data) ? data : [];
      setBatches(normalizedBatches);

      const salesEntries = await Promise.all(
        normalizedBatches.map(async (batch) => {
          const sales = await henBatchesAPI.getSalesHistory(batch.id);
          return [batch.id, sales] as const;
        })
      );
      setSalesByBatch(Object.fromEntries(salesEntries));
    } catch (err: any) {
      setError('Failed to load hen batches');
      console.error(err);
      setBatches([]); // Set to empty array on error
      setSalesByBatch({});
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingBatch) {
        await henBatchesAPI.updateHenBatch(editingBatch.id, {
          batch_name: formData.batch_name,
          current_count: formData.current_count,
          age_weeks: formData.age_weeks,
          age_days: formData.age_days,
          notes: formData.notes || undefined,
        });
        setEditingBatch(null);
      } else {
        await henBatchesAPI.createHenBatch(formData);
        setShowAddForm(false);
      }
      setFormData({
        batch_name: '',
        initial_count: 0,
        current_count: 0,
        age_weeks: 0,
        age_days: 0,
        date_added: new Date().toISOString().split('T')[0],
        notes: '',
      });
      fetchBatches();
    } catch (err: any) {
      setError(err.response?.data?.error || `Failed to ${editingBatch ? 'update' : 'create'} batch`);
    }
  };

  const handleEdit = (batch: HenBatch) => {
    setEditingBatch(batch);
    setFormData({
      batch_name: batch.batch_name,
      initial_count: batch.initial_count,
      current_count: batch.current_count,
      age_weeks: batch.age_weeks,
      age_days: batch.age_days,
      date_added: batch.date_added.split('T')[0],
      notes: batch.notes || '',
    });
    setShowAddForm(false);
  };

  const handleDelete = async (batch: HenBatch) => {
    if (!window.confirm(`Are you sure you want to delete batch "${batch.batch_name}"? This action cannot be undone.`)) {
      return;
    }
    try {
      await henBatchesAPI.deleteHenBatch(batch.id);
      fetchBatches();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete batch');
    }
  };

  const handleCancel = () => {
    setEditingBatch(null);
    setShowAddForm(false);
    setFormData({
      batch_name: '',
      initial_count: 0,
      current_count: 0,
      age_weeks: 0,
      age_days: 0,
      date_added: new Date().toISOString().split('T')[0],
      notes: '',
    });
  };

  const formatAge = (weeks: number, days: number) => {
    if (weeks === 0 && days === 0) return 'Day 0 (Chicks)';
    if (days === 0) return `${weeks}W`;
    return `${weeks}W ${days}D`;
  };

  const getCalculatedAge = (batch: HenBatch) => {
    // Use the initial age stored in the batch (age_weeks, age_days) when it was added
    // and add the elapsed time since date_added
    return calculateAgeFromDate(batch.date_added, timezone, batch.age_weeks, batch.age_days);
  };

  const handleAddMortality = (batchId: number) => {
    setMortalityBatchId(batchId);
    setMortalityFormData({
      mortality_date: new Date().toISOString().split('T')[0],
      count: 0,
      reason: '',
      notes: '',
    });
    setShowMortalityForm(true);
  };

  const handleMortalitySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mortalityBatchId) return;

    try {
      await henBatchesAPI.createMortality({
        batch_id: mortalityBatchId,
        mortality_date: mortalityFormData.mortality_date,
        count: mortalityFormData.count,
        reason: mortalityFormData.reason || undefined,
        notes: mortalityFormData.notes || undefined,
      });
      setShowMortalityForm(false);
      setMortalityBatchId(null);
      setMortalityFormData({
        mortality_date: new Date().toISOString().split('T')[0],
        count: 0,
        reason: '',
        notes: '',
      });
      fetchBatches(); // Refresh batches to show updated counts
      // Refresh chart if it's open for this batch
      if (showMortalityChart && selectedBatchId === mortalityBatchId) {
        // Chart will auto-refresh when batchId changes
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to record mortality');
    }
  };

  const handleAddSale = (batchId: number) => {
    setSaleBatchId(batchId);
    setSaleFormData({
      sale_date: new Date().toISOString().split('T')[0],
      count: 0,
      price_per_hen: 0,
      notes: '',
    });
    setShowSaleForm(true);
  };

  const handleSaleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!saleBatchId) return;

    try {
      await henBatchesAPI.createSale({
        batch_id: saleBatchId,
        sale_date: saleFormData.sale_date,
        count: saleFormData.count,
        price_per_hen: saleFormData.price_per_hen,
        notes: saleFormData.notes || undefined,
      });
      setShowSaleForm(false);
      setSaleBatchId(null);
      fetchBatches();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to record sale');
    }
  };

  const getVisibleRecentSale = (batchId: number) => {
    const sales = salesByBatch[batchId] || [];
    if (sales.length === 0) return null;

    const latestSale = [...sales].sort((a, b) => b.sale_date.localeCompare(a.sale_date))[0];
    const saleDate = new Date(latestSale.sale_date);
    const now = new Date();
    const diffDays = Math.floor((now.getTime() - saleDate.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays <= 21) {
      return latestSale;
    }
    return null;
  };

  const getTotalSold = (batchId: number) => {
    return (salesByBatch[batchId] || []).reduce((sum, sale) => sum + (sale.count || 0), 0);
  };

  // Safely calculate total count, handling null/undefined batches
  const totalCount = (batches || []).reduce((sum, batch) => sum + (batch?.current_count || 0), 0);

  const getAgeCategory = (weeks: number): 'chick' | 'grower' | 'pre-layer' | 'layer' => {
    const chickMax = tenant?.age_category_chick_max_weeks ?? 6;
    const growerMax = tenant?.age_category_grower_max_weeks ?? 18;
    const preLayerMax = tenant?.age_category_prelayer_max_weeks ?? 22;

    if (weeks < chickMax) return 'chick';
    if (weeks < growerMax) return 'grower';
    if (weeks < preLayerMax) return 'pre-layer';
    return 'layer';
  };

  const getCategoryLabel = (category: 'chick' | 'grower' | 'pre-layer' | 'layer'): string => {
    const chickMax = tenant?.age_category_chick_max_weeks ?? 6;
    const growerMax = tenant?.age_category_grower_max_weeks ?? 18;
    const preLayerMax = tenant?.age_category_prelayer_max_weeks ?? 22;

    switch (category) {
      case 'chick':
        return `Chick (0-${chickMax} weeks)`;
      case 'grower':
        return `Grower (${chickMax}-${growerMax} weeks)`;
      case 'pre-layer':
        return `Pre-Layer (${growerMax}-${preLayerMax} weeks)`;
      case 'layer':
        return `Layer (${preLayerMax}+ weeks)`;
    }
  };

  const categoryData = (batches || []).reduce((acc, batch) => {
    const age = getCalculatedAge(batch);
    const category = getAgeCategory(age.weeks);
    const count = batch?.current_count || 0;

    if (!acc[category]) {
      acc[category] = { count: 0, batches: [] };
    }
    acc[category].count += count;
    acc[category].batches.push(batch);

    return acc;
  }, {} as Record<'chick' | 'grower' | 'pre-layer' | 'layer', { count: number; batches: HenBatch[] }>);

  return (
    <div className="hen-batches-page">
      <div className="page-header">
        <div>
          <h1>Hen Batches</h1>
          <p className="page-subtitle">Total Head Count: <strong>{totalCount.toLocaleString()}</strong></p>
        </div>
        {canAddBatch && (
          <button onClick={() => setShowAddForm(!showAddForm)} className="add-btn">
            {showAddForm ? 'Cancel' : '+ Add Batch'}
          </button>
        )}
      </div>

      {(showAddForm || editingBatch) && canAddBatch && (
        <div className="add-batch-form">
          <h2>{editingBatch ? 'Edit Hen Batch' : 'Add New Hen Batch'}</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Batch Name</label>
                <input
                  type="text"
                  value={formData.batch_name}
                  onChange={(e) => setFormData({ ...formData, batch_name: e.target.value })}
                  placeholder="e.g., Batch 1, Batch A"
                  required
                />
              </div>
              <div className="form-group">
                <label>Date Added</label>
                <input
                  type="date"
                  value={formData.date_added}
                  onChange={(e) => {
                    const newDate = e.target.value;
                    // Auto-calculate age when date changes (only for new batches, not editing)
                    // For new batches, we start with 0 weeks/days, so calculate elapsed time only
                    if (!editingBatch && newDate) {
                      const age = calculateAgeFromDate(newDate, timezone, 0, 0);
                      setFormData({ 
                        ...formData, 
                        date_added: newDate,
                        age_weeks: age.weeks,
                        age_days: age.days
                      });
                    } else {
                      setFormData({ ...formData, date_added: newDate });
                    }
                  }}
                  required
                  disabled={!!editingBatch}
                />
              </div>
            </div>
            <div className="form-row">
              {!editingBatch && (
                <div className="form-group">
                  <label>Initial Head Count</label>
                  <input
                    type="number"
                    value={formData.initial_count ?? ''}
                    onChange={(e) => {
                      const count = e.target.value === '' ? 0 : (parseInt(e.target.value) ?? 0);
                      setFormData({ ...formData, initial_count: count, current_count: count });
                    }}
                    required
                    min="0"
                  />
                </div>
              )}
              <div className="form-group">
                <label>Current Head Count</label>
                <input
                  type="number"
                  value={formData.current_count ?? ''}
                  onChange={(e) => {
                    const count = e.target.value === '' ? 0 : (parseInt(e.target.value) ?? 0);
                    setFormData({ ...formData, current_count: count });
                  }}
                  required
                  min="0"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Age (Weeks) <span className="hint">(Auto-calculated from date, or set manually for existing batches)</span></label>
                <input
                  type="number"
                  value={formData.age_weeks ?? ''}
                  onChange={(e) => {
                    const value = e.target.value === '' ? 0 : (parseInt(e.target.value) ?? 0);
                    setFormData({ ...formData, age_weeks: value });
                  }}
                  min="0"
                  required
                />
              </div>
              <div className="form-group">
                <label>Age (Days) <span className="hint">(Auto-calculated from date, or set manually for existing batches)</span></label>
                <input
                  type="number"
                  value={formData.age_days ?? ''}
                  onChange={(e) => {
                    const value = e.target.value === '' ? 0 : (parseInt(e.target.value) ?? 0);
                    setFormData({ ...formData, age_days: value });
                  }}
                  min="0"
                  max="6"
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <label>Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                placeholder="Additional details about this batch..."
              />
            </div>
            <div className="form-actions">
              <button type="submit" className="submit-btn">
                {editingBatch ? 'Update Batch' : 'Add Batch'}
              </button>
              <button type="button" onClick={handleCancel} className="cancel-btn">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      {/* Mortality Form Modal */}
      {showMortalityForm && mortalityBatchId && (
        <div className="modal-overlay" onClick={() => setShowMortalityForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Record Mortality</h2>
              <button className="close-btn" onClick={() => setShowMortalityForm(false)}>
                ×
              </button>
            </div>
            <form onSubmit={handleMortalitySubmit}>
              <div className="form-group">
                <label>Mortality Date</label>
                <input
                  type="date"
                  value={mortalityFormData.mortality_date}
                  onChange={(e) => setMortalityFormData({ ...mortalityFormData, mortality_date: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Count</label>
                <input
                  type="number"
                  value={mortalityFormData.count ?? ''}
                  onChange={(e) => {
                    const count = e.target.value === '' ? 0 : (parseInt(e.target.value) ?? 0);
                    setMortalityFormData({ ...mortalityFormData, count });
                  }}
                  min="1"
                  required
                />
              </div>
              <div className="form-group">
                <label>Reason (Optional)</label>
                <input
                  type="text"
                  value={mortalityFormData.reason}
                  onChange={(e) => setMortalityFormData({ ...mortalityFormData, reason: e.target.value })}
                  placeholder="e.g., Disease, Heat stress, etc."
                />
              </div>
              <div className="form-group">
                <label>Notes (Optional)</label>
                <textarea
                  value={mortalityFormData.notes}
                  onChange={(e) => setMortalityFormData({ ...mortalityFormData, notes: e.target.value })}
                  placeholder="Additional notes..."
                  rows={3}
                />
              </div>
              <div className="form-actions">
                <button type="submit" className="btn-primary">
                  Record Mortality
                </button>
                <button type="button" className="btn-secondary" onClick={() => setShowMortalityForm(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Mortality Chart Modal */}
      {showSaleForm && saleBatchId && (
        <div className="modal-overlay" onClick={() => setShowSaleForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Mark Batch As Sold</h2>
              <button className="close-btn" onClick={() => setShowSaleForm(false)}>
                ×
              </button>
            </div>
            <form onSubmit={handleSaleSubmit}>
              <div className="form-group">
                <label>Sale Date</label>
                <input
                  type="date"
                  value={saleFormData.sale_date}
                  onChange={(e) => setSaleFormData({ ...saleFormData, sale_date: e.target.value })}
                  title="Sale date"
                  aria-label="Sale date"
                  required
                />
              </div>
              <div className="form-group">
                <label>Sold Count</label>
                <input
                  type="number"
                  value={saleFormData.count ?? ''}
                  onChange={(e) => setSaleFormData({ ...saleFormData, count: parseInt(e.target.value || '0', 10) || 0 })}
                  min="1"
                  title="Sold count"
                  aria-label="Sold count"
                  required
                />
              </div>
              <div className="form-group">
                <label>Price Per Hen (₹)</label>
                <input
                  type="number"
                  step="0.01"
                  value={saleFormData.price_per_hen ?? ''}
                  onChange={(e) => setSaleFormData({ ...saleFormData, price_per_hen: parseFloat(e.target.value || '0') || 0 })}
                  min="0"
                  title="Price per hen"
                  aria-label="Price per hen"
                  required
                />
              </div>
              <div className="form-group">
                <label>Notes (Optional)</label>
                <textarea
                  value={saleFormData.notes}
                  onChange={(e) => setSaleFormData({ ...saleFormData, notes: e.target.value })}
                  rows={3}
                  placeholder="Sale details..."
                  title="Sale notes"
                  aria-label="Sale notes"
                />
              </div>
              <div className="form-actions">
                <button type="submit" className="btn-primary">
                  Save Sale
                </button>
                <button type="button" className="btn-secondary" onClick={() => setShowSaleForm(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Mortality Chart Modal */}
      {showMortalityChart && selectedBatchId && (
        <MortalityChart
          batchId={selectedBatchId}
          batchName={selectedBatchName}
          onClose={() => {
            setShowMortalityChart(false);
            setSelectedBatchId(null);
            setSelectedBatchName('');
          }}
        />
      )}

      {loading ? (
        <div className="loading">Loading hen batches...</div>
      ) : batches.length === 0 ? (
        <div className="no-data">
          {canAddBatch ? 'No hen batches found. Add your first batch above.' : 'No hen batches found.'}
        </div>
      ) : (
        <>
          <div className="age-categories">
            <h2>Hens by Age Category</h2>
            <div className="category-cards">
              {(['chick', 'grower', 'pre-layer', 'layer'] as const).map((category) => (
                <div key={category} className={`category-card ${category}`}>
                  <div className="category-label">{getCategoryLabel(category)}</div>
                  <div className="category-count">
                    {(categoryData[category]?.count || 0).toLocaleString()}
                  </div>
                  <div className="category-percentage">
                    {totalCount > 0
                      ? ((categoryData[category]?.count || 0) / totalCount * 100).toFixed(1)
                      : '0'}%
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="batches-grid">
            {batches.map((batch) => {
              const age = getCalculatedAge(batch);
              const category = getAgeCategory(age.weeks);
              return (
                <div key={batch.id} className={`batch-card ${category}`}>
                  {getVisibleRecentSale(batch.id) && (
                    <div className="sold-badge">
                      SOLD {getVisibleRecentSale(batch.id)?.count?.toLocaleString()} on{' '}
                      {formatDateForTenant(getVisibleRecentSale(batch.id)!.sale_date, timezone, dateFormat)}
                    </div>
                  )}
                  <div className="batch-header">
                    <h3>{batch.batch_name}</h3>
                    <div className="batch-header-right">
                      <span className={`category-badge ${category}`}>
                        {getCategoryLabel(category).split(' ')[0]}
                      </span>
                      <span className="batch-age">{formatAge(age.weeks, age.days)}</span>
                      {canAddBatch && (
                        <div className="batch-actions">
                          <button
                            onClick={() => handleEdit(batch)}
                            className="edit-btn"
                            title="Edit batch"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => handleDelete(batch)}
                            className="delete-btn"
                            title="Delete batch"
                          >
                            🗑️
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="batch-details">
                    <div className="detail-item">
                      <span className="label">Current Count:</span>
                      <span className="value">{batch.current_count.toLocaleString()}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Initial Count:</span>
                      <span className="value">{batch.initial_count.toLocaleString()}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Reduction (Mortality + Sold):</span>
                      <span className="value mortality">
                        {(batch.initial_count - batch.current_count).toLocaleString()}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Total Sold:</span>
                      <span className="value">{getTotalSold(batch.id).toLocaleString()}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Date Added:</span>
                      <span className="value">
                        {formatDateForTenant(batch.date_added, timezone, dateFormat)}
                      </span>
                    </div>
                    <div className="batch-actions-bottom">
                      {canAddBatch && (
                        <>
                          <button
                            onClick={() => handleAddMortality(batch.id)}
                            className="mortality-btn"
                            title="Record Mortality"
                          >
                            📉 Record Mortality
                          </button>
                          <button
                            onClick={() => handleAddSale(batch.id)}
                            className="sale-btn"
                            title="Mark as Sold"
                          >
                            💰 Mark as Sold
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => {
                          setSelectedBatchId(batch.id);
                          setSelectedBatchName(batch.batch_name);
                          setShowMortalityChart(true);
                        }}
                        className="chart-btn"
                        title="View Mortality Chart"
                      >
                        📊 View Chart
                      </button>
                    </div>
                    {batch.notes && (
                      <div className="batch-notes">
                        <span className="label">Notes:</span>
                        <span className="value">{batch.notes}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default HenBatchesPage;



